// Dashboard/index.jsx

import React, { useState } from 'react';
import {
  DialogContent,
  Box,
  Grid
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';

import SkipRate from './SkipRate';
import Accuracy from './Accuracy';
import Fluency from './Fluency';
import './styles.css';

// Chart types enum for expanded state
const CHART_TYPES = {
    ACCURACY: 'accuracy',
    SKIP_RATE: 'skipRate',
    FLUENCY: 'fluency',
    NONE: 'none'
};

// Debug logging
const DEBUG_MODE = false;
const log = (...args) => {
  if (DEBUG_MODE) {
    console.log('[Dashboard]', ...args);
  }
};

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    '&::-webkit-scrollbar': {
      display: 'none'
    },
    '-ms-overflow-style': 'none',
    scrollbarWidth: 'none',
    overflowY: 'auto',
    padding: '0',
    marginTop: 0,
    flex: 1, 
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '100%',
    width: '100%'
  },
  contentBox: {
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflowY: 'auto',
    maxWidth: '100%',
    width: '100%'
  },
  gridContainer: {
    marginTop: 0,
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'nowrap',
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
    transition: 'all 0.8s linear',
    alignItems: 'stretch',
    gap: '8px'
  },
  // For default view where all items are equal
  chartGridItem: {
    display: 'flex',
    flex: '0 0 32%',
    padding: '4px',
    boxSizing: 'border-box'
  },
  // For expanded view where one item is much larger
  expandedChartItem: {
    flex: '0 0 78%',
    maxWidth: '78%'
  },
  collapsedChartItem: {
    flex: '0 0 10%',
    maxWidth: '10%',
    minWidth: '80px'
  }
}));

// Accept props directly from parent and books from Redux
const DashboardDataContainer = ({ accuracy, skipRate, fluency, loading, error, books, student }) => {
    const classes = useStyles();
    
    // State to track which chart is expanded (if any)
    const [expandedChart, setExpandedChart] = useState(CHART_TYPES.NONE);

    // Handle chart expansion
    const handleExpandChart = (chartType) => {
        log('Expanding chart:', chartType);
        if (expandedChart === chartType) {
            setExpandedChart(CHART_TYPES.NONE);
        } else {
            setExpandedChart(chartType);
        }
    };

    return (
        <DialogContent className={classes.dialogContent}>
            <Box className={classes.contentBox} style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                {/* Grid layout with metrics */}
                <div 
                    className={`${classes.gridContainer} dashboard-charts-container`}
                    style={{ 
                        maxWidth: '100%', 
                        width: '100%', 
                        boxSizing: 'border-box',
                        display: 'flex',
                        flexDirection: 'row'
                    }}
                >
                    {/* Accuracy Chart */}
                    <div 
                        className={`${classes.chartGridItem} ${
                            expandedChart === CHART_TYPES.ACCURACY 
                                ? classes.expandedChartItem 
                                : expandedChart !== CHART_TYPES.NONE 
                                    ? classes.collapsedChartItem 
                                    : ''
                        } chart-grid-item`}
                    >
                        <Accuracy 
                            data={accuracy} 
                            loading={loading ? loading.accuracy : false} 
                            error={error}
                            books={books}
                            student={student}
                            isExpanded={expandedChart === CHART_TYPES.ACCURACY}
                            isCollapsed={expandedChart !== CHART_TYPES.NONE && expandedChart !== CHART_TYPES.ACCURACY}
                            onExpand={() => handleExpandChart(CHART_TYPES.ACCURACY)}
                        />
                    </div>
                    
                    {/* Skip Rate Chart */}
                    <div 
                        className={`${classes.chartGridItem} ${
                            expandedChart === CHART_TYPES.SKIP_RATE 
                                ? classes.expandedChartItem 
                                : expandedChart !== CHART_TYPES.NONE 
                                    ? classes.collapsedChartItem 
                                    : ''
                        } chart-grid-item`}
                    >
                        <SkipRate 
                            data={skipRate} 
                            loading={loading ? loading.skipRate : false} 
                            error={error}
                            books={books}
                            student={student}
                            isExpanded={expandedChart === CHART_TYPES.SKIP_RATE}
                            isCollapsed={expandedChart !== CHART_TYPES.NONE && expandedChart !== CHART_TYPES.SKIP_RATE}
                            onExpand={() => handleExpandChart(CHART_TYPES.SKIP_RATE)}
                        />
                    </div>
                    
                    {/* Fluency Chart */}
                    <div 
                        className={`${classes.chartGridItem} ${
                            expandedChart === CHART_TYPES.FLUENCY 
                                ? classes.expandedChartItem 
                                : expandedChart !== CHART_TYPES.NONE 
                                    ? classes.collapsedChartItem 
                                    : ''
                        } chart-grid-item`}
                    >
                        <Fluency 
                            data={fluency} 
                            loading={loading ? loading.fluency : false} 
                            error={error}
                            books={books}
                            student={student}
                            isExpanded={expandedChart === CHART_TYPES.FLUENCY}
                            isCollapsed={expandedChart !== CHART_TYPES.NONE && expandedChart !== CHART_TYPES.FLUENCY}
                            onExpand={() => handleExpandChart(CHART_TYPES.FLUENCY)}
                        />
                    </div>
                </div>
            </Box>
        </DialogContent>
    );
};

// Map Redux state to props - following the same pattern as Books.jsx
const mapState = ({ platform: { books } }) => {
    return {
        books
    }
};

// No dispatch actions needed for now
const mapDispatch = (dispatch) => {
    return {}
};

export default connect(mapState, mapDispatch)(DashboardDataContainer);